/**
 * @deprecated: This file is deprecated and will be removed in the future.
 * Use the new `iotu/v2_auth` API service instead.
 * This file contains the legacy login and token refresh logic
 * Backwards fallback while migrating to the new API service
 *
 * Issue: https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2449
 */
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

import { user_data } from 'src/stores/account/user';
import { firmwares_gen_2 } from 'src/stores/device-runtime/process';
import { isRetrying, RetryStatus } from 'src/stores/session/failed-requests';
import { loadingData } from 'src/stores/ui/loading-data';

import qs from 'qs';
import jwt_decode from 'jwt-decode';

import { api, cloud, handle_base_url, handleToken } from '../../http';

import { handleLogout } from 'src/helpers/account_helpers';

import { resetDrawer } from 'src/helpers/drawer_helpers';

import { toast } from '@Shelly/shelly-design-system/index';
import CacheStatisticRequest from 'src/helpers/statistics/request/chart_cache_statistics_helpers';

import type { TokenData } from 'src/types/TokenData';
import { capFirstChar } from 'src/helpers/utilities';
import { setUpI18n } from '../../i18n';
import type { UserData } from 'src/types/UserData';
import { legacy_prepare_wss_address } from '../../socket';
import { normalizeEmail } from 'src/helpers/email_helpers';

/**
 * Standart login v1 (PHP API)
 * @deprecated
 * Request is made to the global API or Cloud Instance API
 * Standard login requests should be made to the global API server
 * Token refresh requests should be made to the user-instance server
 *
 * Fallback to global API request if the Instance request fails
 *
 * @param email
 * @param pass
 * @param refreshToken bool - true to use the instance API, false to use global API
 * @returns
 */
export const v1_standardLogin = async (email, pass, refreshToken?: boolean) => {
	// Normalize email to lowercase to ensure consistent handling across the app
	email = normalizeEmail(email);

	const apiInstance = refreshToken ? cloud : api;
	try {
		const res = await apiInstance.post(
			'/auth/login',
			qs.stringify({
				email: email,
				password: pass,
				var: 2,
			}),
			{ headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
		);
		let result = res.data;
		console.log('[AUTH] Standard login response: ', result, result.data.token);

		const tokenData: TokenData = jwt_decode(result.data.token);
		result.data = {
			...result.data,
			notifications_urls: v1_fix_notifications_urls(result.data.notifications_urls),
			email: email,
			password: pass,
			user_id: tokenData.user_id,
		};
		console.log('[AUTH] Reponse after normalization: ', result);

		v1_handleInitialUserdata(result.data);
		if (!refreshToken) {
			/**
			 * Clear caches here.
			 * We need to figure out what other storages must be cleared
			 *
			 */
			CacheStatisticRequest.clear();
			firmwares_gen_2.reset();
			resetDrawer('login');
		}

		return result;
	} catch (error) {
		console.log('[AUTH] Standard login catch: ', error);
		if (error?.status >= 500) {
			return { isok: false, errors: { server_error: true } };
		}
		if (!error.response?.data?.errors) {
			return { isok: false, errors: { no_connection: true } };
		}
		// ts.iliev:
		// Max-attempts handling in legacy API was not fully implemented
		// You can skip this part
		//
		if (
			error.response?.status === 400 &&
			error.response?.data?.errors.hasOwnProperty('max_attempts') &&
			get(user_data).email === email
		) {
			let alertProps = handleLegacyLoginErrors(error.response.data.errors, email);
			toast.open({
				type: 'danger',
				title: alertProps.heading,
				text: alertProps.message,
			});
			return handleLogout();
		}
		return error?.response?.data;
	}
};

/**
 * Handle initial user data after login
 * - Sets the user data in the store
 * - Sets the base URL for the user API instance
 * - Sets the token for the API
 * - Clears caches
 * - Sets up i18n
 * - Handles user profile
 *
 * @param {UserData} user_data - The user data to set
 */
const v1_handleInitialUserdata = async (data: UserData) => {
	// Set the user data in the store
	user_data.set(data);
	// Set the base URL for the API
	handle_base_url(data.user_api_url);
	// Set the token for the API
	handleToken(data.token);

	await setUpI18n(data.lang);
};

/**
 *
 * Refresh token v1 (PHP API)
 * @deprecated
 */
export const v1_crefresh_token = async () => {
	// try {

	if (get(isRetrying) === RetryStatus.IN_PROGRESS) {
		return;
	}

	isRetrying.update(RetryStatus.IN_PROGRESS);
	const _user_data = get(user_data);
	const res = await v1_standardLogin(_user_data.email, _user_data.password, true);

	if (!res.isok) {
		isRetrying.update(RetryStatus.INVALID);

		if (res.errors.wrong_credentials) {
			const title = get(format)('session_expired', {
				default: 'Session expired',
			});
			const text = get(format)('session_expired_description', {
				default: 'Please log in again.',
			});

			toast.open({
				title,
				text,
				type: 'danger',
			});

			loadingData.set(false);
			return handleLogout();
		}

		// Error handling: skip user_data update on error
		return Promise.reject(res);
	}

	user_data.set(res.data);
	handle_base_url(res.data.user_api_url);
	handleToken(res.data.token);

	isRetrying.update(RetryStatus.INVALID);
	return res.data;
	// } catch (error) {
	//console.log('Error on refresh tokens');
	// return false;
	// }
};

/**
 * @deprecated Should be removed after migration to IOTU v2 login
 *
 * Fixes the URLs for notifications, comming from the PHP API
 * * Correct WS protocol: wss://
 * * Correct port: 6113
 * * Correct path: /shelly/wss/hk_sock
 * @param urls - Array of URLs to fix
 * @returns {string[]} - Array of fixed URLs
 */
export const v1_fix_notifications_urls = (urls: string[]) => {
	return urls.map((url) => {
		return legacy_prepare_wss_address(url);
	});
};

/**
 * Handle Login Errors for PHP-API (Legacy) login endpoints
 *
 * @todo: Remove this function after complete migration of the logins
 * Impartant: Used also for `auth/installer_login` error handling
 *
 * Handles the errors returned by the legacy login API
 * and returns an object with the heading and message for the alert.
 * @param errors
 * @param email
 * @returns { heading: string, message: string } Alert props
 */
export function handleLegacyLoginErrors(
	errors,
	email?: string,
): {
	heading: string;
	message: string;
} {
	const alertProps = {
		heading: get(format)('alert_danger_error_heading', { default: 'Error' }),
		message: get(format)('err_msg_please_try_again', {
			default: 'Please try again!',
		}),
	};
	if (errors.wrong_credentials) {
		alertProps.heading = get(format)('error_wrong_credentials', {
			default: `Error, ${errors.wrong_credentials}`,
		});
		alertProps.message = get(format)('no_account_desc', {
			default: "Sorry, we couldn't find an account with that email and password.",
		});
	} else if (errors.max_attempts) {
		alertProps.heading = get(format)('alert_danger_login_error_heading', {
			default: 'Login error',
		});
		alertProps.message = capFirstChar(
			email
				? get(format)('locked_account_desc_with_email', {
						default:
							'Too many incorrect login attempts. Your account ({email}) is temporarily locked for one hour.',
						values: { email },
					})
				: get(format)('locked_account_desc', {
						default:
							'Too many incorrect login attempts. Your account is temporarily locked for one hour.',
					}),
		);
	} else if (errors.no_connection) {
		alertProps.heading = get(format)('error_no_connection', {
			default: 'Connection Error',
		});
		alertProps.message = get(format)('no_connection_desc', {
			default: 'Network connectivity problem. Please, try again.',
		});
	} else if (errors.server_error) {
		alertProps.message = get(format)('server_error_desc', {
			default: 'Server could not be reached. Please, try again.',
		});
	} else if (errors.invalid_token) {
		alertProps.message = get(format)('error_user_invalid_token', {
			default: errors.invalid_token,
		});
	}
	return alertProps;
}
